import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { FlowerPink, FlowerRed, FlowerBlue, FlowerYellow, FlowerLavender, LeafSprig } from "@/components/FlowerDeco";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden meadow-wash paper-grain"
    >
      {/* Soft watercolor landscape underlay - subtle */}
      <div
        className="absolute inset-0 opacity-30 mix-blend-multiply pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 1100px 600px at 30% 70%, rgba(74, 124, 71, 0.4), transparent 70%), radial-gradient(ellipse 900px 500px at 80% 30%, rgba(212, 175, 55, 0.25), transparent 70%)",
        }}
      />

      {/* Colorful Ghibli flowers around the hero */}
      <FlowerPink className="absolute top-12 left-[3%] w-24 h-24 opacity-95 animate-float-slow z-10" />
      <FlowerYellow className="absolute top-6 left-[14%] w-16 h-16 opacity-90 animate-drift z-10" />
      <FlowerBlue className="absolute bottom-32 left-[4%] w-20 h-20 opacity-90 animate-float-slow z-10" style={{ animationDelay: "1s" }} />
      <FlowerRed className="absolute bottom-40 left-[14%] w-14 h-14 opacity-90 animate-drift z-10" style={{ animationDelay: "0.4s" }} />
      <FlowerLavender className="absolute bottom-8 left-[22%] w-14 h-24 opacity-95 z-10" />
      <LeafSprig className="absolute bottom-12 left-[6%] w-40 opacity-60 z-10" />

      <FlowerRed className="absolute top-20 right-[5%] w-20 h-20 opacity-95 animate-float-slow z-10" />
      <FlowerPink className="absolute top-40 right-[18%] w-14 h-14 opacity-90 animate-drift z-10" style={{ animationDelay: "1.2s" }} />
      <FlowerLavender className="absolute bottom-20 right-[6%] w-14 h-24 opacity-95 z-10" />
      <FlowerYellow className="absolute bottom-32 right-[22%] w-12 h-12 opacity-95 animate-float-slow z-10" style={{ animationDelay: "0.6s" }} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-24 lg:pt-28 lg:pb-32 z-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-6 animate-fade-up">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.32em] text-brand-gold mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              From our family to yours
            </span>

            <h1 className="font-serif text-5xl sm:text-6xl lg:text-[64px] leading-[1.04] tracking-tight text-brand-moss">
              Handcrafted <span className="italic doodle-underline text-brand-moss-deep">warmth</span> for your home,
              <br />
              <span className="font-script text-brand-gold text-6xl lg:text-7xl">from our family</span>{" "}
              <span className="text-brand-moss/90">to yours.</span>
            </h1>

            <p className="mt-7 max-w-xl text-lg leading-relaxed text-brand-moss/80 font-sans">
              A small, faith-driven studio pouring candles, arranging florals, and blending growth
              oils by hand — in small batches, with a whole lot of prayer.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/shop"
                data-testid="hero-cta-explore-shop"
                className="group inline-flex items-center justify-center gap-2 bg-brand-moss text-brand-gold hover:bg-brand-moss-light transition-colors px-8 py-4 rounded-full text-base tracking-wide shadow-md"
              >
                Explore Our Sanctuary Shop
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/membership"
                data-testid="hero-cta-membership"
                className="inline-flex items-center justify-center gap-2 border border-brand-moss/30 text-brand-moss hover:bg-brand-moss/5 transition-colors px-8 py-4 rounded-full text-base tracking-wide"
              >
                Join the Circle
              </Link>
            </div>

            <div className="mt-12 flex items-center gap-6 text-sm text-brand-moss/60">
              <span className="font-script text-2xl text-brand-gold">414</span>
              <span className="h-px flex-1 bg-brand-moss/20 max-w-[120px]" />
              <span className="tracking-wide">Faith · Purpose · Community</span>
            </div>
          </div>

          <div className="lg:col-span-6 relative h-[440px] lg:h-[580px]">
            {/* Real brand product-lineup hero image */}
            <div className="absolute inset-0 rounded-[2.5rem] overflow-hidden shadow-2xl border-4 border-brand-cream rotate-[-1.5deg]">
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 hover:scale-105"
                style={{ backgroundImage: "url('/assets/product_lineup.jpg')" }}
              />
            </div>

            {/* Trust badge */}
            <div className="absolute -bottom-4 left-2 rotate-[-3deg] bg-brand-cream backdrop-blur-sm border border-brand-moss/15 rounded-2xl px-5 py-4 shadow-xl max-w-[240px] z-10">
              <p className="font-script text-2xl text-brand-moss leading-none">Hand-poured</p>
              <p className="text-xs uppercase tracking-[0.25em] text-brand-gold mt-2">small-batch · prayed over</p>
            </div>

            {/* Small floral accent */}
            <img
              src="/assets/flower_cosmos.png"
              alt=""
              aria-hidden="true"
              className="absolute -top-8 -right-4 w-40 opacity-90 animate-float-slow pointer-events-none"
            />
          </div>
        </div>
      </div>

      {/* Bottom soft wave */}
      <svg
        viewBox="0 0 1440 80"
        preserveAspectRatio="none"
        className="absolute bottom-[-1px] left-0 w-full h-16 text-brand-cream z-10"
        aria-hidden="true"
      >
        <path
          d="M0,40 C220,5 440,75 720,40 C1000,5 1220,75 1440,30 L1440,80 L0,80 Z"
          fill="currentColor"
        />
      </svg>
    </section>
  );
}
